import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import useInvRole from './useInvRole';
import { isStoreChoiceLocked } from '../utils/assignedStores';

const ActiveStoreContext = createContext(null);

/** Keeps the current pick when it is still assigned; otherwise the first assigned store. */
function pickActiveId(assignedIds, preferred) {
  const assigned = Array.isArray(assignedIds) ? assignedIds.map(String) : [];
  if (!assigned.length) return preferred ? String(preferred) : '';
  const want = preferred ? String(preferred) : '';
  return want && assigned.includes(want) ? want : assigned[0];
}

export function ActiveStoreProvider({ children }) {
  const { myStoreIds } = useInvRole();
  const [activeStoreId, setActiveStoreIdState] = useState(() => pickActiveId(myStoreIds));

  useEffect(() => {
    setActiveStoreIdState((prev) => pickActiveId(myStoreIds, prev));
  }, [myStoreIds]);

  const setActiveStoreId = useCallback((id) => {
    const next = id ? String(id) : '';
    if (myStoreIds.length && next && !myStoreIds.includes(next)) return;
    setActiveStoreIdState(next);
  }, [myStoreIds]);

  const value = useMemo(() => ({
    assignedStoreIds: myStoreIds,
    activeStoreId,
    setActiveStoreId,
    storeLocked: isStoreChoiceLocked(myStoreIds),
    isStoreScoped: myStoreIds.length > 0,
  }), [myStoreIds, activeStoreId, setActiveStoreId]);

  return (
    <ActiveStoreContext.Provider value={value}>
      {children}
    </ActiveStoreContext.Provider>
  );
}

export default function useActiveStore() {
  const ctx = useContext(ActiveStoreContext);
  const { myStoreIds } = useInvRole();
  if (ctx) return ctx;
  // Screens outside InvLayout (e.g. OT charge capture) have no provider to switch in.
  return {
    assignedStoreIds: myStoreIds,
    activeStoreId: pickActiveId(myStoreIds),
    setActiveStoreId: () => {},
    storeLocked: isStoreChoiceLocked(myStoreIds),
    isStoreScoped: myStoreIds.length > 0,
  };
}
